import React, { useState } from 'react';
import { Input, Select, Button, Tooltip, Empty } from 'antd';
import { BellFilled, BellOutlined, CaretDownFilled, CloseOutlined, EditOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from "@src/components/spinner";
import { getMobileServiceNotesByTaskIdQuery } from '../../util/query/mobileResponse.query';
import { addMobileServiceNoteMutation, removeMobileServiceNoteReminderMutation, updateMobileServiceNoteMutation } from '../../util/mutation/mobileServices.mutation';
import { setNotification } from '@src/middleware/redux/reducers/notification.reducer';

dayjs.extend(relativeTime);

// App theme tokens (same coral/teal palette used by drawer-filled-btn / drawer-outlined-btn
// across the rest of the app — see src/pages/formDrawer/drawer.css)
const BRAND = {
  coral: 'rgb(255, 122, 89)',
  coralHover: 'rgb(255, 143, 115)',
  coralSoft: 'rgb(255, 248, 246)',
  coralBorder: 'rgba(255, 122, 89, 0.35)',
  coralTint: 'rgba(255, 122, 89, 0.08)',
  teal: '#0D5C63',
  muted: 'rgb(81, 111, 144)',
  disabledBg: 'rgb(234, 240, 246)',
  disabledText: 'rgb(176, 193, 212)',
};

const REMINDER_REASONS = [
  { value: 'Chase Employee', label: 'Chase Employee' },
  { value: 'Chase Supplier', label: 'Chase Supplier' },
  { value: 'Chase Customer', label: 'Chase Customer' },
  { value: 'System/Data Issue', label: 'System Issue' },
  { value: 'Other', label: 'Other' },
];

const REMINDER_DELAYS = [
  { value: 1, label: 'In 1 minute (test)' },
  { value: 30, label: 'In 30 minutes' },
  { value: 60, label: 'In 1 hour' },
  { value: 120, label: 'In 2 hours' },
  { value: 240, label: 'In 4 hours' },
  { value: 1440, label: 'Next day' },
];

const EDIT_WINDOW_MINUTES = 5;

const AVATAR_COLORS = [BRAND.coral, BRAND.teal, '#0091ae', '#4f46e5', '#10b981', '#f59e0b'];

const colorFromString = (str) => {
  if (!str) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (createdByDetail) => {
  if (createdByDetail?.firstname) return createdByDetail.firstname.charAt(0).toUpperCase();
  if (createdByDetail?.email) return createdByDetail.email.charAt(0).toUpperCase();
  return 'U';
};

const getDisplayName = (createdByDetail) => {
  if (createdByDetail?.firstname) {
    return `${createdByDetail.firstname} ${createdByDetail.lastname || ''}`.trim();
  }
  return createdByDetail?.email || 'Unknown user';
};

const Avatar = ({ createdByDetail, size = 32 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: colorFromString(getDisplayName(createdByDetail)),
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: size * 0.4, fontWeight: 600,
  }}>
    {getInitials(createdByDetail)}
  </div>
);

// Ghost icon button with a hover color shift toward the brand coral — done in
// JS state since the rest of this file uses inline styles, not a stylesheet.
const GhostIconButton = ({ icon, onClick, title, loading, danger }) => {
  const [hover, setHover] = useState(false);
  const color = loading ? BRAND.disabledText : hover ? BRAND.coral : (danger ? 'rgb(197, 87, 61)' : '#9ca3af');
  return (
    <Tooltip title={title}>
      <button
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        disabled={loading}
        style={{
          width: 22, height: 22, borderRadius: '50%', border: 'none',
          background: hover ? BRAND.coralTint : 'transparent',
          color, display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: loading ? 'default' : 'pointer', padding: 0, flexShrink: 0,
          transition: 'all 0.15s ease',
        }}
      >
        {icon}
      </button>
    </Tooltip>
  );
};

const ActivityTab = ({ taskData }) => {
  const dispatch = useDispatch();
  const taskId = taskData?._id;

  const { authenticatedUserDetail } = useSelector(state => state.userAuthReducer);
  const currentUserId = authenticatedUserDetail?._id;

  const [note, setNote] = useState('');
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderReason, setReminderReason] = useState('Chase Employee');
  const [remindInMinutes, setRemindInMinutes] = useState(60);

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');

  const { data, loading, refetch } = useQuery(getMobileServiceNotesByTaskIdQuery, {
    variables: { taskId },
    skip: !taskId,
    fetchPolicy: 'network-only',
  });

  const notes = data?.getMobileServiceNotesByTaskId?.response || [];

  const [addMobileServiceNote, { loading: addLoading }] = useMutation(addMobileServiceNoteMutation);
  const [updateMobileServiceNote, { loading: updateLoading }] = useMutation(updateMobileServiceNoteMutation);
  const [removeMobileServiceNoteReminder, { loading: removeLoading }] = useMutation(removeMobileServiceNoteReminderMutation);
  const [removingId, setRemovingId] = useState(null);

  const notify = (error, message) => dispatch(setNotification({ error, notificationState: true, message }));

  const resetComposer = () => {
    setNote('');
    setReminderOpen(false);
    setReminderReason('Chase Employee');
    setRemindInMinutes(60);
  };

  const handleSubmit = async () => {
    if (!note.trim()) return;
    try {
      await addMobileServiceNote({
        variables: {
          input: {
            taskId,
            note: note.trim(),
            reminderReason: reminderOpen ? reminderReason : null,
            remindInMinutes: reminderOpen ? remindInMinutes : null,
          },
        },
      });
      resetComposer();
      refetch();
      notify(false, 'Activity note added');
    } catch (err) {
      notify(true, err?.message || 'Failed to add note');
    }
  };

  const isOwnNote = (entry) => entry?.createdByDetail?._id && currentUserId && entry.createdByDetail._id === currentUserId;

  const canEdit = (entry) =>
    isOwnNote(entry) && entry.createdAt && dayjs().diff(dayjs(entry.createdAt), 'minute', true) < EDIT_WINDOW_MINUTES;

  const canRemoveReminder = (entry) =>
    isOwnNote(entry) && entry.reminderReason && !entry.reminderSent;

  const startEdit = (entry) => {
    setEditingNoteId(entry._id);
    setEditText(entry.note);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditText('');
  };

  const saveEdit = async (noteId) => {
    if (!editText.trim()) return;
    try {
      await updateMobileServiceNote({ variables: { input: { noteId, note: editText.trim() } } });
      cancelEdit();
      refetch();
      notify(false, 'Note updated');
    } catch (err) {
      notify(true, err?.message || 'Failed to update note');
    }
  };

  const handleRemoveReminder = async (noteId) => {
    setRemovingId(noteId);
    try {
      await removeMobileServiceNoteReminder({ variables: { noteId } });
      await refetch();
      notify(false, 'Reminder removed');
    } catch (err) {
      notify(true, err?.message || 'Failed to remove reminder');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div style={{ padding: '4px 2px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>

      {/* Composer */}
      <div style={{
        background: '#fff', border: '1px solid #e8eaed', borderRadius: 14,
        padding: 14, marginBottom: 20, boxShadow: '0 1px 3px rgba(16,24,40,0.05)',
      }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <Avatar createdByDetail={authenticatedUserDetail} />
          <Input.TextArea
            rows={3}
            variant="borderless"
            placeholder="Log what's happening on this duty — why it's not complete, who you contacted…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ background: '#f7f8fa', borderRadius: 10, padding: '10px 12px', resize: 'none' }}
          />
        </div>

        {/* Reminder builder — collapsed to a single toggle chip until opened */}
        {reminderOpen ? (
          <div style={{
            marginTop: 10, background: BRAND.coralTint, border: `1px solid ${BRAND.coralBorder}`,
            borderRadius: 10, padding: '10px 12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: BRAND.coral, display: 'flex', alignItems: 'center', gap: 6 }}>
                <BellFilled /> Remind if still not completed
              </span>
              <GhostIconButton icon={<CloseOutlined style={{ fontSize: 11 }} />} title="Cancel reminder" onClick={() => setReminderOpen(false)} />
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontSize: 11, color: BRAND.muted, marginBottom: 3, fontWeight: 500 }}>Reason</div>
                <Select
                  size="middle"
                  style={{ width: '100%' }}
                  className='custom-select'
                  value={reminderReason}
                  onChange={setReminderReason}
                  options={REMINDER_REASONS}
                  suffixIcon={<CaretDownFilled/>}

                />
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontSize: 11, color: BRAND.muted, marginBottom: 3, fontWeight: 500 }}>Remind me</div>
                <Select
                  size="middle"
                  className='custom-select'
                  style={{ width: '100%' }}
                  value={remindInMinutes}
                  onChange={setRemindInMinutes}
                  options={REMINDER_DELAYS}
                  suffixIcon={<CaretDownFilled/>}
                />
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setReminderOpen(true)}
            style={{
              marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#fff', border: `1px solid ${BRAND.coralBorder}`, color: BRAND.coral,
              borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 500,
              cursor: 'pointer', transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = BRAND.coralSoft}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
          >
            <BellOutlined /> Add reminder
          </button>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          marginTop: 12, paddingTop: 10, borderTop: '1px solid #f1f2f4',
        }}>
          <Button
            className={(!note.trim() || addLoading) ? 'disabled-btn' : 'drawer-filled-btn'}
            disabled={!note.trim() || addLoading}
            loading={addLoading}
            onClick={handleSubmit}
            style={{ borderRadius: 999, padding: '6px 18px', height: 'auto', fontSize: 13 }}
          >
           
            <SendOutlined /> &nbsp;
            Post

          </Button>
        </div>
      </div>

      {/* Feed */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>
      ) : notes.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No activity logged for this duty yet"
          style={{ padding: '24px 0' }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {notes.map((entry, i) => {
            const editing = editingNoteId === entry._id;
            const removing = removingId === entry._id && removeLoading;
            return (
              <div key={entry._id || i} style={{ display: 'flex', gap: 12 }}>

                {/* Rail */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <Avatar createdByDetail={entry.createdByDetail} />
                  {i < notes.length - 1 && (
                    <div style={{ width: 2, flex: 1, background: '#eef0f3', marginTop: 4, borderRadius: 1 }} />
                  )}
                </div>

                {/* Card */}
                <div style={{ flex: 1, paddingBottom: 20, minWidth: 0 }}>
                  <div style={{
                    background: '#fff', border: '1px solid #eef0f3', borderRadius: 12,
                    padding: '10px 14px', boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
                        {getDisplayName(entry.createdByDetail)}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Tooltip title={entry.createdAt ? dayjs(entry.createdAt).format('DD MMM YYYY · HH:mm') : ''}>
                          <span style={{ fontSize: 12, color: '#9ca3af' }}>
                            {entry.createdAt ? dayjs(entry.createdAt).fromNow() : '---'}
                          </span>
                        </Tooltip>
                        {!editing && canEdit(entry) && (
                          <GhostIconButton
                            icon={<EditOutlined style={{ fontSize: 12 }} />}
                            title="Edit (within 5 minutes of posting)"
                            onClick={() => startEdit(entry)}
                          />
                        )}
                      </div>
                    </div>

                    {editing ? (
                      <div style={{ marginTop: 6 }}>
                        <Input.TextArea
                          rows={2}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          autoFocus
                          style={{ borderRadius: 8 }}
                        />
                        <div style={{ marginTop: 6, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <Button className="drawer-outlined-btn" style={{ borderRadius: 999, padding: '4px 14px', height: 'auto', fontSize: 12 }} onClick={cancelEdit}>
                            Cancel
                          </Button>
                          <Button
                            className={(!editText.trim() || updateLoading) ? 'disabled-btn' : 'drawer-filled-btn'}
                            style={{ borderRadius: 999, padding: '4px 14px', height: 'auto', fontSize: 12 }}
                            loading={updateLoading}
                            disabled={!editText.trim()}
                            onClick={() => saveEdit(entry._id)}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.55, whiteSpace: 'pre-wrap', marginTop: 4 }}>
                        {entry.note}
                      </div>
                    )}

                    {entry.reminderReason && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        marginTop: 10, padding: '6px 10px', borderRadius: 10,
                        background: entry.reminderSent ? '#f9fafb' : BRAND.coralTint,
                        border: `1px solid ${entry.reminderSent ? '#e5e7eb' : BRAND.coralBorder}`,
                      }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: entry.reminderSent ? '#e5e7eb' : BRAND.coral,
                          color: entry.reminderSent ? '#9ca3af' : '#fff',
                        }}>
                          <BellFilled style={{ fontSize: 11 }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: entry.reminderSent ? '#6b7280' : '#9a3412' }}>
                            {entry.reminderReason}
                          </div>
                          {entry.remindAt && (
                            <div style={{ fontSize: 11, color: '#9ca3af' }}>
                              {entry.reminderSent ? 'Reminder sent' : `Reminder ${dayjs(entry.remindAt).fromNow()}`}
                            </div>
                          )}
                        </div>
                        {canRemoveReminder(entry) && (
                          <GhostIconButton
                            icon={<CloseOutlined style={{ fontSize: 11 }} />}
                            title="Remove reminder"
                            loading={removing}
                            danger
                            onClick={() => handleRemoveReminder(entry._id)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
